import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static bodyModalClass = "lightbox-open"
  static targets = ["overlay", "image", "spinner", "prevButton", "nextButton"]

  connect() {
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundPreviewClick = this.handlePreviewClick.bind(this)
    this.boundImageLoad = this.onImageLoad.bind(this)
    this.boundImageError = this.onImageError.bind(this)
    this.element.addEventListener("click", this.boundPreviewClick)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    this.element.removeEventListener("click", this.boundPreviewClick)
    this.unlockBodyScroll()
    if (this.hasImageTarget) {
      this.imageTarget.removeEventListener("load", this.boundImageLoad)
      this.imageTarget.removeEventListener("error", this.boundImageError)
    }
  }

  handlePreviewClick(event) {
    const thumbShell = event.target.closest(".image-upload-thumb-shell")
    if (thumbShell && this.element.contains(thumbShell)) {
      const wrapper = thumbShell.closest("[data-signed-id], [data-file-id]")
      if (!wrapper || !this.element.contains(wrapper)) return
      if (event.target.closest(".remove-upload-btn") || event.target.closest("[data-action*='image-crop']")) {
        return
      }
      this.openFromElement(wrapper)
      return
    }

    const wrapper = event.target.closest("[data-signed-id], [data-file-id]")
    if (!wrapper || !this.element.contains(wrapper)) return
    if (event.target.closest("[data-image-upload-detail]") || event.target.closest("[data-image-alt-save]")) {
      return
    }
    const tag = event.target.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "LABEL" || tag === "SELECT") return
    if (event.target.closest(".remove-upload-btn") || event.target.closest("[data-action*='image-crop']")) {
      return
    }
    this.openFromElement(wrapper)
  }

  openFromElement(elementOrEvent) {
    const el = elementOrEvent?.currentTarget || elementOrEvent
    const src = el?.dataset?.fullSrc || el?.src || el?.querySelector?.("img")?.src
    if (!src) return

    // Build images array from gallery DOM, or single image (prefer full-size URLs)
    let images = []
    let index = 0

    const gallery =
      el.closest("[data-image-upload-target='uploads']") ||
      el.closest("[data-lightbox-gallery]")
    if (gallery) {
      const wrappers = gallery.querySelectorAll("[data-signed-id], [data-file-id]")
      images = Array.from(wrappers).map((w) => w.dataset.fullSrc || w.querySelector("img")?.src).filter(Boolean)
      index = Math.max(0, images.indexOf(src))
    } else {
      images = [src]
      index = 0
    }

    this.images = images
    this.index = index
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("flex")
    this.lockBodyScroll()
    document.addEventListener("keydown", this.boundKeydown)
    this.showImage()
  }

  showImage() {
    if (!this.images?.length) return
    this.index = ((this.index % this.images.length) + this.images.length) % this.images.length
    const src = this.images[this.index]

    this.showSpinner()
    this.imageTarget.alt = `Image ${this.index + 1} of ${this.images.length}`

    if (this.imageTarget.src !== src) {
      this.imageTarget.removeEventListener("load", this.boundImageLoad)
      this.imageTarget.removeEventListener("error", this.boundImageError)
      this.imageTarget.addEventListener("load", this.boundImageLoad)
      this.imageTarget.addEventListener("error", this.boundImageError)
    }
    this.imageTarget.src = src

    if (this.imageTarget.complete) {
      this.hideSpinner()
    }

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("invisible", this.images.length <= 1)
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("invisible", this.images.length <= 1)
    }
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
      this.spinnerTarget.classList.add("flex")
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
      this.spinnerTarget.classList.remove("flex")
    }
  }

  onImageLoad() {
    this.hideSpinner()
    this.imageTarget.removeEventListener("load", this.boundImageLoad)
    this.imageTarget.removeEventListener("error", this.boundImageError)
  }

  onImageError() {
    this.hideSpinner()
    this.imageTarget.removeEventListener("load", this.boundImageLoad)
    this.imageTarget.removeEventListener("error", this.boundImageError)
  }

  prev() {
    if (!this.images?.length || this.images.length <= 1) return
    this.index -= 1
    this.showImage()
  }

  next() {
    if (!this.images?.length || this.images.length <= 1) return
    this.index += 1
    this.showImage()
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("flex")
    this.unlockBodyScroll()
    document.removeEventListener("keydown", this.boundKeydown)
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
    else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.prev()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    }
  }

  lockBodyScroll() {
    document.body.classList.add(this.constructor.bodyModalClass)
  }

  unlockBodyScroll() {
    document.body.classList.remove(this.constructor.bodyModalClass)
  }
}
